<template>
  <validation-provider
    #default="{ errors }"
    :name="label"
    :vid="label"
    :rules="rules"
  >
    <b-form-group
      :label="label"
      :label-for="name"
      :class="{ disabled: readonly || disabled }"
    >
      <v-select
        ref="select"
        key="value"
        v-model="inputValue"
        :name="name"
        :options="computedOptions"
        v-bind="$attrs"
        :placeholder="placeholder"
        append-to-body
        label="text"
        group-label="group"
        group-values="options"
        :multiple="multiple"
        :state="errors.length > 0 ? false : null"
        :disabled="disabled"
        @change="onChange"
        @open="onOpen"
      >
        <div slot="no-options">
          Nenhum(a) {{ label }} encontrado(a).
        </div>
      </v-select>
      <small class="text-danger">{{ errors[0] }}</small>
      <item-help
        v-if="help"
        :help="help"
        class="float-right position-relative l-0"
      />
    </b-form-group>
  </validation-provider>
</template>

<script>
import { required } from "@validations";
import ItemHelp from "@/components/i2/forms/elements/ItemHelp.vue";
import { ValidationProvider } from "vee-validate";
export default {
  name: "I2FormVueSelect",
  components: {
    ItemHelp,
    ValidationProvider,
  },
  props: {
    label: {
      type: String,
      required: true,
    },
    value: {
      type: [String, Number, Object, Array],
      default: "",
    },
    name: {
      type: String,
      required: true,
    },
    placeholder: {
      type: String,
      required: false,
      default: "",
    },
    options: {
      type: Array,
      required: true,
    },
    rules: {
      type: String,
      default: "",
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    help: {
      type: String,
      default: "",
      required: false,
    },

    valueField: {
      type: Boolean,
      default: false,
      required: false,
    },
    // Grouping: when provided, groups flat options by the given key into
    // [{ label, options: [...] }] structure understood by vue-select.
    // Example: groupBy="group" and each option has a 'group' property.
    groupBy: {
      type: [String, Function],
      default: null,
      required: false,
    },
  },
  data() {
    return {
      inputValue: "",
      selected: null,
      required,
    };
  },
  computed: {
    computedOptions() {
      const opts = this.options || [];
      if (!this.groupBy) return opts;

      // Build groups from flat options
      const getGroup =
        typeof this.groupBy === "function"
          ? this.groupBy
          : o => (o && typeof o === "object" ? o[this.groupBy] : undefined);

      const map = new Map();
      opts.forEach(o => {
        const g = getGroup(o) || "Outros";
        if (!map.has(g)) map.set(g, []);
        map.get(g).push(o);
      });
      // vue-select usa group-label="group" e group-values="options"
      return Array.from(map.entries()).map(([group, options]) => ({
        group,
        options,
      }));
    },
  },
  mounted() {
    // Initialize internal model respecting valueField mode
    if (this.valueField) {
      this.inputValue = this.normalizeToOption(this.value);
    } else {
      this.inputValue = this.value;
    }
  },
  watch: {
    value(data) {
      // When parent value changes, keep internal model consistent
      if (this.valueField) {
        const next = this.normalizeToOption(data);
        // Evita loops quando o v-model já está sincronizado (especialmente em multiple)
        if (!this.isSameValue(this.inputValue, next)) {
          this.inputValue = next;
        }
      } else {
        if (!this.isSameValue(this.inputValue, data)) {
          this.inputValue = data;
        }
      }
    },
    inputValue(val) {
      if (this.valueField) {
        // Emit only the primitive value when valueField is enabled
        const primitive = Array.isArray(val)
          ? val
              .map(v => (v && typeof v === "object" ? v.value : v))
              .filter(v => v !== undefined)
          : val && typeof val === "object"
          ? val.value
          : val;
        // Evita emissão redundante que pode causar loop em múltipla seleção
        if (!this.isSameValue(this.value, primitive ?? null)) {
          this.$emit("input", primitive ?? null);
          this.$emit("onChange", primitive ?? null);
        }
      } else {
        if (!this.isSameValue(this.value, val)) {
          this.$emit("input", val);
          this.$emit("onChange", val);
        }
      }
    },
  },
  methods: {
    isSameValue(a, b) {
      // Shallow compare primitives; array compare by values; object compare by reference
      if (Array.isArray(a) || Array.isArray(b)) {
        const aa = Array.isArray(a) ? a : a == null ? [] : [a];
        const bb = Array.isArray(b) ? b : b == null ? [] : [b];
        if (aa.length !== bb.length) return false;
        for (let i = 0; i < aa.length; i++) {
          if (aa[i] !== bb[i]) return false;
        }
        return true;
      }
      return a === b;
    },
    getFlatOptions() {
      const opts = this.options || [];
      // If grouped via prop, options are still provided as flat; but if parent
      // passed pre-grouped options, flatten here to ensure value resolution
      if (
        Array.isArray(opts) &&
        opts.length &&
        opts[0] &&
        typeof opts[0] === "object" &&
        Array.isArray(opts[0].options) &&
        (opts[0].label !== undefined || opts[0].group !== undefined)
      ) {
        return opts.reduce((acc, g) => acc.concat(g.options || []), []);
      }
      return opts;
    },
    normalizeToOption(val) {
      // For valueField mode, convert primitive(s) to option object(s) expected by v-select
      const opts = this.getFlatOptions();
      if (this.multiple) {
        const values = Array.isArray(val) ? val : val != null ? [val] : [];
        return values
          .map(v =>
            opts.find(o => (o && typeof o === "object" ? o.value : o) === v)
          )
          .filter(Boolean);
      }
      if (val === undefined || val === null || val === "") return null;
      return (
        opts.find(o => (o && typeof o === "object" ? o.value : o) === val) ||
        null
      );
    },
    onChange(data) {
      this.$emit("onChange", data);
    },
    onOpen() {
      setTimeout(() => {
        // Acesse o dropdown diretamente com o ref e modifique o estilo
        const dropdownMenu =
          this.$refs.select.$el.querySelector(".vs__dropdown-menu");
        if (dropdownMenu) {
          this.zIndex = 50000;
          dropdownMenu.style.zIndex = "500000 !important"; // Ajuste o z-index
          dropdownMenu.style.borderColor = "red !important"; // Ajuste o z-index
          dropdownMenu.style.position = "absolute !important"; // Ajuste o z-index
        }
      }, 200);
    },
  },
};
</script>
<style lang="scss">
@import "~@core/scss/vue/libs/vue-select.scss";
.vs__dropdown-menu {
  z-index: 9999999 !important;
  border-color: red !important;
}
</style>
